// src/components/NotificationSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import { getToken } from 'firebase/messaging';
import { doc, setDoc } from 'firebase/firestore';
import { messaging, db } from '../firebase';

const VAPID_KEY = 'BN-T58CIdOACoVlgKckTQ62D5aUO0YpU7ng5AbnDAezHO4DI5Fz1u7S5g4fkDCw2hi6idxBRrxYuPoXhZMa8IXk';

async function saveTokenToFirestore(uid, token) {
  const userDocRef = doc(db, 'users', uid);
  // setDoc with merge:true creates the doc if it doesn't exist,
  // and stores token as a map key so duplicates are naturally deduplicated.
  await setDoc(
    userDocRef,
    { fcmTokens: { [token]: true } },
    { merge: true }
  );
}

async function registerToken(uid) {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported by this browser.');
  }
  // Waiting for serviceWorker.ready is critical on mobile PWAs —
  // without this, getToken can fail because the SW isn't active yet.
  const registration = await navigator.serviceWorker.ready;

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    throw new Error('Failed to retrieve FCM token. Check VAPID key and service worker.');
  }

  await saveTokenToFirestore(uid, token);
  return token;
}

function NotificationSettings({ user }) {
  const [permission, setPermission] = useState(
    'Notification' in window ? Notification.permission : 'default'
  );
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const handleEnableNotifications = async () => {
    if (!user?.uid) {
      console.error('No user logged in');
      return;
    }

    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        await registerToken(user.uid);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError('Something went wrong enabling notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isSupported = 'Notification' in window && 'serviceWorker' in navigator;

  if (!isSupported) {
    return (
      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsOffIcon color="disabled" />
          Push Notifications
        </Typography>
        <Alert severity="warning">
          Push notifications are not supported on this device or browser.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {permission === 'granted' ? (
          <NotificationsActiveIcon color="primary" />
        ) : (
          <NotificationsOffIcon color="disabled" />
        )}
        Push Notifications
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Receive instant notifications when you're assigned new jobs or when job statuses change.
      </Typography>

      {permission === 'granted' ? (
        <Box>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Push notifications are enabled on this device!
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              You'll receive notifications even when the app is closed.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleEnableNotifications}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <SyncIcon />}
              sx={{ borderRadius: 2 }}
            >
              {loading ? 'Syncing...' : 'Re-sync Device'}
            </Button>
          </Box>
        </Box>
      ) : permission === 'denied' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            Notifications are blocked. Please enable them in your browser settings.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            To enable: Click the lock icon in the address bar → Site settings → Allow notifications
          </Typography>
        </Box>
      ) : (
        <Box>
          <Button
            variant="contained"
            onClick={handleEnableNotifications}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <NotificationsActiveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              animation: loading ? 'none' : 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.4)' },
                '70%': { boxShadow: '0 0 0 10px rgba(59, 130, 246, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)' },
              },
            }}
          >
            {loading ? 'Enabling...' : 'Enable Push Notifications'}
          </Button>
        </Box>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Device synced successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
}

export default NotificationSettings;